import { useCallback, useEffect, useRef } from 'react';
import type { PlayerProfile } from '@goodtime/engine';
import { getProfile, putProfile } from '../api/profile';

/** How long to coalesce rapid mutations before pushing. PlayerProfile
 *  persists on EVERY change (every coin, gem, setting), so without this
 *  a burst of spins would hammer the server; 3s batches them into one PUT. */
const PUSH_DEBOUNCE_MS = 3000;

/**
 * Client half of profile sync (2026-06-09). Backs the full
 * {@link PlayerProfile} snapshot up to the server under the player's
 * (now persisted) id, and restores it on a reinstall.
 *
 * <p>Two responsibilities:
 * <ul>
 *   <li><b>Hydrate</b> ({@link hydrateFromServer}, called once from the boot
 *       effect when the player id resolves): on a reinstall — local cache
 *       empty at boot but the server has a backup — adopt the server
 *       snapshot. Otherwise establish/refresh the backup from local state.</li>
 *   <li><b>Push</b> (this hook's effect): after any profile change, debounce
 *       a PUT. Gated on a "hydrated" flag so a pre-hydrate default snapshot
 *       can never clobber a real server backup.</li>
 * </ul>
 *
 * <p>Wallet stays authoritative for currency — the boot effect runs the
 * wallet reconcile AFTER hydrate, correcting coins/gems on top.
 *
 * <p>Failures are best-effort + silent (console.warn): this is background
 * backup, not a user action, so a failed push doesn't warrant a toast — the
 * next change re-pushes, and the local cache is never at risk in the
 * meantime. {@code playerId === null} → no-op (pure-solo / pre-connect).
 */
export function useProfileSync(
  profile: PlayerProfile,
  playerId: string | null,
  hadStoredProfileAtBoot: boolean,
): { hydrateFromServer: () => Promise<boolean> } {
  // Stable ref so the push effect doesn't re-subscribe on every render
  // (the profile is a stable useMemo anyway, but this matches useWalletSync).
  const profileRef = useRef(profile);
  profileRef.current = profile;

  // Don't push until the initial hydrate has settled — otherwise the
  // default snapshot a fresh PlayerProfile boots with could overwrite the
  // server backup before we've had a chance to adopt it (the reinstall race).
  const pushReadyRef = useRef(false);

  const hydrateFromServer = useCallback(async (): Promise<boolean> => {
    if (!playerId) return false;
    const p = profileRef.current;
    try {
      const stored = await getProfile(playerId);
      if (stored && !hadStoredProfileAtBoot) {
        // Reinstall recovery: nothing in the local cache at boot, but the
        // server holds a backup under our persisted id. Adopt it silently;
        // the boot effect refreshes the React mirrors from the getters after.
        p.hydrate(stored.profile);
        pushReadyRef.current = true;
        return true;
      }
      // Fresh player, or a local cache is present (single-device authority):
      // establish / refresh the server backup from local state.
      await putProfile(playerId, p.snapshot());
      pushReadyRef.current = true;
      return false;
    } catch (e) {
      console.warn('[useProfileSync] hydrate failed; using local profile', e);
      // Enable pushing anyway so later changes still attempt a backup.
      pushReadyRef.current = true;
      return false;
    }
  }, [playerId, hadStoredProfileAtBoot]);

  useEffect(() => {
    if (!playerId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const flush = (): void => {
      timer = null;
      if (!pushReadyRef.current) return; // guard the reinstall race (see above)
      void putProfile(playerId, profileRef.current.snapshot()).catch((e) => {
        console.warn('[useProfileSync] push failed; will retry on next change', e);
      });
    };

    const sub = profileRef.current.events.onAny((event) => {
      // sync-error is informational (and the wallet sync's own failure
      // channel) — pushing on it would be redundant and could itself fail.
      if (event.kind === 'sync-error') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, PUSH_DEBOUNCE_MS);
    });

    return () => {
      sub.dispose();
      if (timer) clearTimeout(timer);
    };
  }, [playerId]);

  return { hydrateFromServer };
}
